import React, { useState, useEffect } from 'react';
import { Users, Clock, AlertTriangle, CheckCircle, Stethoscope, Heart, Thermometer, Activity } from 'lucide-react';
import { useAlert } from './contexts/AlertContext';
import { useAuth } from './contexts/AuthContext';
import apiService from './apiService';
import useWebSocket from './hooks/useWebSocket';
import TriageRoutingModal from './TriageRoutingModal';

const NurseTriage = ({ onNavigate }) => {
    const { currentUser } = useAuth();
    const { showAlert } = useAlert();
    const [waitingPatients, setWaitingPatients] = useState([]);
    const [currentPatient, setCurrentPatient] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [vitalSigns, setVitalSigns] = useState({
        bloodPressure: '',
        heartRate: '',
        temperature: '',
        oxygenSaturation: '',
        weight: '',
        height: ''
    });
    const [triageNotes, setTriageNotes] = useState('');
    const [triageLevel, setTriageLevel] = useState('medium');
    const [triageQueue, setTriageQueue] = useState({ nowServing: null, waitingQueue: [], totalWaiting: 0 });
    const [showRoutingModal, setShowRoutingModal] = useState(false);

    useEffect(() => {
        fetchWaitingPatients();
    }, []);

    // WebSocket for real-time patient and queue updates
    useWebSocket({
        onMessage: (message) => {
            // Refresh waiting patients when updates occur
            if (message.type === 'patient_status_changed' || 
                message.type === 'queue_updated' ||
                message.type === 'patient_checked_in') {
                console.log('Nurse triage received update:', message.type);
                fetchWaitingPatients();
            }
        },
        onConnect: () => {
            console.log('Nurse triage connected to real-time updates');
        }
    });

    const fetchWaitingPatients = async () => {
        setLoading(true);
        try {
            // Get triage queue using queue management API
            const queue = await apiService.getQueueByLocation('triage');
            console.log('Queue response:', queue);
            
            // Handle response structure properly
            const queueData = queue?.queueByLocation || { nowServing: null, waitingQueue: [], totalWaiting: 0 };
            setTriageQueue(queueData);
            setCurrentPatient(queueData.nowServing);
            
            // Also get general waiting patients as backup
            const patients = await apiService.getWaitingPatients();
            setWaitingPatients(patients);
        } catch (error) {
            console.error('Error fetching waiting patients:', error);
            // Set default empty state on error
            setTriageQueue({ nowServing: null, waitingQueue: [], totalWaiting: 0 });
            setCurrentPatient(null);
            showAlert('Failed to load triage queue. Showing all waiting patients.', 'warning');
            
            // Try to at least get waiting patients
            try {
                const patients = await apiService.getWaitingPatients();
                setWaitingPatients(patients);
            } catch (innerError) {
                console.error('Failed to fetch waiting patients:', innerError);
            }
        } finally {
            setLoading(false);
        }
    };
    
    const handleCallNextPatient = async () => {
        try {
            const result = await apiService.callNextPatient('triage', 'Triage Room 1');
            console.log('✅ Called next patient:', result);
            
            if (result.callNextPatient.success) {
                showAlert(result.callNextPatient.message, 'success');
                await fetchWaitingPatients(); // Refresh queue
            } else {
                showAlert(result.callNextPatient.message, 'info');
            }
        } catch (error) {
            console.error('Error calling next patient:', error);
            showAlert('Failed to call next patient', 'error');
        }
    };

    const handlePatientSelect = (patient) => {
        setSelectedPatient(patient);
        setVitalSigns({
            bloodPressure: '',
            heartRate: '',
            temperature: '',
            oxygenSaturation: '',
            weight: '',
            height: ''
        });
        setTriageNotes('');
        setTriageLevel('medium');
    };

    const handleVitalSignsChange = (field, value) => {
        setVitalSigns(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const getTriageColor = (level) => {
        switch (level) {
            case 'critical': return 'bg-red-100 text-red-800 border-red-200';
            case 'high': return 'bg-orange-100 text-orange-800 border-orange-200';
            case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
            case 'low': return 'bg-green-100 text-green-800 border-green-200';
            default: return 'bg-gray-100 text-gray-800 border-gray-200';
        }
    };

    const getTriageIcon = (level) => {
        switch (level) {
            case 'critical': return <AlertTriangle className="w-4 h-4" />;
            case 'high': return <AlertTriangle className="w-4 h-4" />;
            case 'medium': return <Clock className="w-4 h-4" />;
            case 'low': return <CheckCircle className="w-4 h-4" />;
            default: return <Clock className="w-4 h-4" />;
        }
    };

    const handleCompleteTriage = async () => {
        if (!selectedPatient) return;

        try {
            // Step 1: Update patient with triage information
            await apiService.updatePatient({
                patientId: selectedPatient.patientId,
                medicalHistory: `${selectedPatient.medicalHistory || ''}\n\nTRIAGE ASSESSMENT (${new Date().toLocaleString()}):\n` +
                    `Priority Level: ${triageLevel.toUpperCase()}\n` +
                    `Vital Signs: BP ${vitalSigns.bloodPressure}, HR ${vitalSigns.heartRate}, Temp ${vitalSigns.temperature}°C, O2 ${vitalSigns.oxygenSaturation}%\n` +
                    `Weight: ${vitalSigns.weight}kg, Height: ${vitalSigns.height}cm\n` +
                    `Notes: ${triageNotes}`
            });

            // Step 2: Show routing modal for nurse to decide next step
            setShowRoutingModal(true);
        } catch (error) {
            console.error('Error updating triage data:', error);
            showAlert('Failed to save triage data', 'error');
        }
    };

    const handleRoutingComplete = async (result) => {
        // Clear selection
        setSelectedPatient(null);
        setCurrentPatient(null);
        setShowRoutingModal(false);
        
        // Refresh queue
        await fetchWaitingPatients();
        
        showAlert(result.message || '✅ Triage completed and patient routed!', 'success');
    };

    const handleAssignToDoctor = async () => {
        if (!selectedPatient) return;

        try {
            // Navigate to appointment scheduling
            onNavigate('add-appointment', { 
                patientId: selectedPatient.id,
                priority: triageLevel,
                notes: `TRIAGE: ${triageNotes}\nVital Signs: BP ${vitalSigns.bloodPressure}, HR ${vitalSigns.heartRate}, Temp ${vitalSigns.temperature}°C`
            });
        } catch (error) {
            console.error('Error assigning to doctor:', error);
            showAlert('Failed to assign to doctor', 'error');
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">Nurse Triage</h2>
                    <p className="text-gray-600">Assess and prioritize waiting patients</p>
                </div>
                <div className="flex items-center gap-4">
                    <button
                        onClick={handleCallNextPatient}
                        className="bg-green-500 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={!triageQueue || triageQueue.totalWaiting === 0}
                    >
                        <Users className="w-4 h-4" />
                        Call Next Patient
                    </button>
                    <button
                        onClick={fetchWaitingPatients}
                        className="bg-blue-500 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-600"
                    >
                        <Users className="w-4 h-4" />
                        Refresh
                    </button>
                </div>
            </div>
            
            {/* Now Serving */}
            {currentPatient && (
                <div className="bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg shadow-lg p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm opacity-90 font-medium">NOW SERVING</p>
                            <h3 className="text-3xl font-bold mt-1">Queue #{String(currentPatient.queueNumber).padStart(3, '0')}</h3>
                            <p className="text-lg mt-2">{currentPatient.name}</p>
                            <p className="text-sm opacity-90">{currentPatient.department || 'General'}</p>
                        </div>
                        <div className="text-right">
                            <Heart className="w-16 h-16 opacity-50 mb-2" />
                            <p className="text-sm">Triage Room 1</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white p-4 rounded-lg shadow-sm border">
                    <div className="flex items-center">
                        <Users className="w-6 h-6 text-blue-500" />
                        <div className="ml-3">
                            <p className="text-sm font-medium text-gray-600">In Queue</p>
                            <p className="text-xl font-bold text-gray-900">{triageQueue?.totalWaiting || 0}</p>
                        </div>
                    </div>
                </div>
                
                <div className="bg-white p-4 rounded-lg shadow-sm border">
                    <div className="flex items-center">
                        <Heart className="w-6 h-6 text-green-500" />
                        <div className="ml-3">
                            <p className="text-sm font-medium text-gray-600">Now Serving</p>
                            <p className="text-xl font-bold text-gray-900">
                                {currentPatient ? `#${String(currentPatient.queueNumber).padStart(3, '0')}` : '-'}
                            </p>
                        </div>
                    </div>
                </div>
                
                <div className="bg-white p-4 rounded-lg shadow-sm border">
                    <div className="flex items-center">
                        <Clock className="w-6 h-6 text-yellow-500" />
                        <div className="ml-3">
                            <p className="text-sm font-medium text-gray-600">Average Wait</p>
                            <p className="text-xl font-bold text-gray-900">
                                {triageQueue?.totalWaiting > 0 ? `${triageQueue.totalWaiting * 5} min` : '-'}
                            </p>
                        </div>
                    </div>
                </div>
                
                <div className="bg-white p-4 rounded-lg shadow-sm border">
                    <div className="flex items-center">
                        <Stethoscope className="w-6 h-6 text-purple-500" />
                        <div className="ml-3">
                            <p className="text-sm font-medium text-gray-600">Completed</p>
                            <p className="text-xl font-bold text-gray-900">
                                {waitingPatients.filter(p => p.status === 'completed').length}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Waiting Patients Queue */}
                <div className="bg-white rounded-lg shadow-sm border">
                    <div className="p-6 border-b">
                        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                            <Users className="w-5 h-5" />
                            Triage Queue ({triageQueue?.totalWaiting || 0} waiting)
                        </h3>
                    </div>
                    <div className="p-6">
                        {!triageQueue || triageQueue.totalWaiting === 0 ? (
                            <div className="text-center py-8 text-gray-500">
                                <Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                                <p>No patients waiting for triage</p>
                                <p className="text-sm mt-2">Patients will appear here after reception check-in</p>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {(triageQueue?.waitingQueue || []).map((patient, index) => (
                                    <div 
                                        key={patient.id} 
                                        className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                                            selectedPatient?.id === patient.id 
                                                ? 'border-blue-500 bg-blue-50' 
                                                : 'hover:bg-gray-50'
                                        }`}
                                        onClick={() => handlePatientSelect(patient)}
                                    >
                                        <div className="flex items-start gap-3">
                                            <div className="flex-shrink-0">
                                                <div className="w-12 h-12 bg-blue-500 text-white rounded-lg flex items-center justify-center font-bold">
                                                    #{String(patient.queueNumber).padStart(3, '0')}
                                                </div>
                                            </div>
                                            <div className="flex-1">
                                                <div className="flex justify-between items-start">
                                                    <div>
                                                        <h4 className="font-semibold text-gray-900">{patient.name}</h4>
                                                        <p className="text-sm text-gray-600">
                                                            {patient.department || 'General'} • {patient.age ? `${patient.age} years` : 'Age N/A'}
                                                        </p>
                                                    </div>
                                                    <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                                        Position: {index + 1}
                                                    </span>
                                                </div>
                                                {patient.symptoms && (
                                                    <p className="text-sm text-gray-500 mt-1">
                                                        {patient.symptoms}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Triage Assessment */}
                <div className="bg-white rounded-lg shadow-sm border">
                    <div className="p-6 border-b">
                        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                            <Stethoscope className="w-5 h-5" />
                            Triage Assessment
                        </h3>
                    </div>
                    <div className="p-6">
                        {!selectedPatient ? (
                            <div className="text-center py-8 text-gray-500">
                                <Stethoscope className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                                <p>Select a patient to begin triage assessment</p>
                            </div>
                        ) : (
                            <div className="space-y-6">
                                {/* Patient Info */}
                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <h4 className="font-semibold text-gray-900">{selectedPatient.name}</h4>
                                    <p className="text-sm text-gray-600">
                                        {selectedPatient.age ? `${selectedPatient.age} years` : 'Age not specified'} • {selectedPatient.phone}
                                    </p>
                                    {selectedPatient.symptoms && (
                                        <p className="text-sm text-gray-500 mt-1">
                                            <strong>Symptoms:</strong> {selectedPatient.symptoms}
                                        </p>
                                    )}
                                </div>

                                {/* Vital Signs */}
                                <div>
                                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                        <Activity className="w-4 h-4" />
                                        Vital Signs
                                    </h4>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Blood Pressure
                                            </label>
                                            <input
                                                type="text"
                                                placeholder="120/80"
                                                value={vitalSigns.bloodPressure}
                                                onChange={(e) => handleVitalSignsChange('bloodPressure', e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            />
                                        </div>
                                        
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Heart Rate (BPM)
                                            </label>
                                            <input
                                                type="number"
                                                placeholder="72"
                                                value={vitalSigns.heartRate}
                                                onChange={(e) => handleVitalSignsChange('heartRate', e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            />
                                        </div>
                                        
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Temperature (°C)
                                            </label>
                                            <input
                                                type="number"
                                                step="0.1"
                                                placeholder="36.5"
                                                value={vitalSigns.temperature}
                                                onChange={(e) => handleVitalSignsChange('temperature', e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            />
                                        </div>
                                        
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                O2 Saturation (%)
                                            </label>
                                            <input
                                                type="number"
                                                placeholder="98"
                                                value={vitalSigns.oxygenSaturation}
                                                onChange={(e) => handleVitalSignsChange('oxygenSaturation', e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            />
                                        </div>
                                        
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Weight (kg)
                                            </label>
                                            <input
                                                type="number"
                                                step="0.1"
                                                placeholder="70"
                                                value={vitalSigns.weight}
                                                onChange={(e) => handleVitalSignsChange('weight', e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            />
                                        </div>
                                        
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Height (cm)
                                            </label>
                                            <input
                                                type="number"
                                                placeholder="170"
                                                value={vitalSigns.height}
                                                onChange={(e) => handleVitalSignsChange('height', e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Triage Level */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Priority Level
                                    </label>
                                    <div className="grid grid-cols-4 gap-2">
                                        {['critical', 'high', 'medium', 'low'].map((level) => (
                                            <button
                                                key={level}
                                                onClick={() => setTriageLevel(level)}
                                                className={`p-2 rounded-lg border text-sm font-medium flex items-center justify-center gap-1 ${
                                                    triageLevel === level 
                                                        ? getTriageColor(level) 
                                                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                                                }`}
                                            >
                                                {getTriageIcon(level)}
                                                {level.charAt(0).toUpperCase() + level.slice(1)}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Triage Notes */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Assessment Notes
                                    </label>
                                    <textarea
                                        rows={3}
                                        placeholder="Enter triage assessment notes..."
                                        value={triageNotes}
                                        onChange={(e) => setTriageNotes(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>

                                {/* Actions */}
                                <div className="flex gap-3">
                                    <button
                                        onClick={handleCompleteTriage}
                                        className="flex-1 bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 flex items-center justify-center gap-2"
                                    >
                                        <CheckCircle className="w-4 h-4" />
                                        Complete Triage
                                    </button>
                                    
                                    <button
                                        onClick={handleAssignToDoctor}
                                        className="flex-1 bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 flex items-center justify-center gap-2"
                                    >
                                        <Stethoscope className="w-4 h-4" />
                                        Assign to Doctor
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            
            {/* Triage Routing Modal */}
            {showRoutingModal && selectedPatient && (
                <TriageRoutingModal
                    patient={selectedPatient}
                    onComplete={handleRoutingComplete}
                    onCancel={() => setShowRoutingModal(false)}
                />
            )}
        </div>
    );
};

export default NurseTriage;
